import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const ReturnInwardChallans = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/return-inward-challans`, getAuthHeaders());
        setDocs(res.data);
      } catch {
        setError('Failed to load Return Inward Challans');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div data-testid="return-inward-challans-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Return Inward DC</h1>
          <p className="text-slate-500 mt-1">Inward Delivery Challans returned due to QC failure</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200">
          {/* Table Header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Return #</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Source IDC</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Vendor</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Company</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-1">Units</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Return Date</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-1">Status</div>
          </div>

          {docs.length === 0 ? (
            <div className="text-center py-16">
              <RotateCcw className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No Return Inward Challans yet</p>
              <p className="text-slate-400 text-sm mt-1">Returns are created from the Inward DC detail page when units fail QC</p>
            </div>
          ) : (
            docs.map((doc) => (
              <div
                key={doc.id}
                data-testid={`return-row-${doc.id}`}
                onClick={() => navigate(`/admin/purchase/return-inward-challan/${doc.id}`)}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer items-center transition-colors"
              >
                <div className="col-span-2 font-medium text-orange-600 text-sm">{doc.return_number}</div>
                <div className="col-span-2 text-slate-700 text-sm font-medium">{doc.source_idc_number || '-'}</div>
                <div className="col-span-2 text-slate-600 text-sm">{doc.vendor_name || '-'}</div>
                <div className="col-span-2 text-slate-600 text-sm">{doc.company_name || '-'}</div>
                <div className="col-span-1 text-center">
                  <span className="inline-flex items-center justify-center w-7 h-7 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                    {(doc.returned_units || []).length}
                  </span>
                </div>
                <div className="col-span-2 text-slate-600 text-sm">{formatDate(doc.return_date)}</div>
                <div className="col-span-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                    {doc.status}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
